import { beiSeitenScroll } from '../utils/seitenScroll';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark, CalendarDays, CalendarPlus, Clock3, Cloud, CloudFog, CloudHail, CloudLightning, CloudMoon,
  CloudRain, CloudSnow, CloudSun, Droplets, ExternalLink, Flame, LucideIcon, MapPin, Moon, Share2, Sun, Users, Wind,
} from 'lucide-react';
import { RegistrationBadge, WeatherForecast, weatherLabel } from '@radevents/shared';
import { DatumsKachel, EventKopfBild, EventKopfBildFlaeche, EventKopfStrecke, StartortKarte } from './EventDetailKopf';
import { UntenVeranstalter, VeranstalterZeilen } from './EventDetailUnten';
import { EventCalendarLink, EventDetailPrimaryAction } from './EventDetailMobile';

/**
 * Event-Seite am Rechner (29.10.2026, Vorbilder aus Mobbin: Luma und Circle für den Kopf, komoot
 * für den Strecken-Block, Airbnb, GetYourGuide und Eventbrite für die Anmeldekarte, Tripadvisor
 * für die Sprungleiste).
 *
 * Derselbe Aufbau wie auf dem Handy (`EventDetailKopf`, `EventDetailUnten`), nur in zwei Spalten:
 * links Kopf, Strecke und die Abschnitte, rechts eine mitlaufende Karte mit Termin, Ort und dem
 * Weg zur Anmeldung. Die Seite rechnet, diese Bausteine zeichnen nur.
 *
 * Jede Angabe steht einmal: das Datum in Kachel und Karte (die Kachel hat kein Jahr), Zeitraum
 * und Saison nur in der Karte, die Streckenzahlen nur in der Kennzahlenzeile über der Karte,
 * nicht noch einmal am Höhenprofil.
 */

// ── Kopf ─────────────────────────────────────────────────────────────────────────────────────

export interface EventDesktopAktionen {
  vorgemerkt: boolean;
  onVormerken: () => void;
  onTeilen: () => void;
  /** Termin in den Kalender (ics); fehlt bei permanenten Events. */
  kalender?: EventCalendarLink;
  /** Redaktion: Re-Enrich, Löschen. */
  extra?: React.ReactNode;
}

export const EventDesktopKopf: React.FC<{
  datum: { monat: string; tag: string; wochentag: string } | null;
  farbe: string;
  kicker: string;
  titel: string;
  abgesagt?: boolean;
  /** „Beliebt“, wenn die Badge-Regel greift. */
  beliebt?: string;
  /** Ort in einer Zeile („Schondra, Bayern“). */
  ort?: string;
  countdown?: string;
  /** Hinweis unter dem Titel; `aktion` steht in derselben Zeile (Ausgebucht: „Fürs nächste Jahr benachrichtigen“). */
  status?: { text: string; ton: 'rot' | 'gelb'; aktion?: React.ReactNode };
  /** Logo oder Schriftzug des Events: klein neben dem Titel. */
  logo?: { url: string; alt: string; onFehler: () => void } | null;
  /**
   * Foto des Events: als breites Titelbild über dem Kopf, die Datumskachel ragt hinein. Steht dort
   * unabhängig von der Strecke; vorher verdrängte eine GPX-Karte das Foto ganz. (Neben dem Titel
   * war es zu klein und riss eine Lücke unter die Knöpfe.)
   */
  foto?: { url: string; fokus: string; nachweis?: string | null; ki?: boolean; onFehler: () => void } | null;
  aktionen: EventDesktopAktionen;
  /** Anmeldestand, Glocke: was unter der Ortszeile steht. */
  children?: React.ReactNode;
}> = ({ datum, farbe, kicker, titel, abgesagt, beliebt, ort, countdown, status, logo, foto, aktionen, children }) => (
  <header className={`edd-kopf${foto ? ' edd-kopf--foto' : ''}`}>
    {foto && (
      <figure className="edd-kopf-foto">
        <img src={foto.url} alt="" onError={foto.onFehler} style={{ objectPosition: foto.fokus }} className={abgesagt ? 'ed-cancelled-img' : undefined} />
        {(foto.nachweis || foto.ki) && <figcaption>{foto.nachweis}{foto.ki && <span className="edk-ki">KI-generiert</span>}</figcaption>}
      </figure>
    )}
    <div className="edd-kopf-haupt">
      <div className="edd-kopf-zeile">
        <DatumsKachel datum={datum} farbe={farbe} abgesagt={abgesagt} className="edd-kachel" />
        <div className="edd-kopf-text">
          <div className="edk-kicker" style={{ color: abgesagt ? '#6A6A6A' : farbe }}>
            <span className="edk-raute" style={{ background: abgesagt ? '#B9B3B3' : farbe }} />{kicker}
            {beliebt && <span className="ecb-popular edd-beliebt"><Flame size={12} aria-hidden />{beliebt}</span>}
          </div>
          <h1 className={`edd-titel${titel.length > 60 ? ' edd-titel--lang' : ''}`} style={abgesagt ? { textDecoration: 'line-through' } : undefined}>{titel}</h1>
          {(ort || countdown) && (
            <div className="edd-unterzeile">
              {ort && <span className="edd-unterzeile-ort"><MapPin size={15} aria-hidden />{ort}</span>}
              {countdown && <span className="edk-countdown">{countdown}</span>}
            </div>
          )}
          {children && <div className="edk-zusatz">{children}</div>}
        </div>
        {logo && <img className="edd-logo" src={logo.url} alt={logo.alt} onError={logo.onFehler} />}
      </div>
      {status && (
        <div className={`edk-status edk-status--${status.ton}${status.aktion ? ' edd-status--mit-aktion' : ''}`}>
          <span role="status">{status.text}</span>
          {status.aktion}
        </div>
      )}
      <div className="edd-aktionen">
        <button type="button" className={`edd-aktion${aktionen.vorgemerkt ? ' edd-aktion--an' : ''}`} aria-pressed={aktionen.vorgemerkt} onClick={aktionen.onVormerken}>
          <Bookmark size={17} fill={aktionen.vorgemerkt ? 'currentColor' : 'none'} aria-hidden />{aktionen.vorgemerkt ? 'Vorgemerkt' : 'Vormerken'}
        </button>
        <button type="button" className="edd-aktion" onClick={aktionen.onTeilen}><Share2 size={17} aria-hidden />Teilen</button>
        {aktionen.kalender && (
          <a className="edd-aktion" href={aktionen.kalender.url} download={aktionen.kalender.filename} onClick={aktionen.kalender.onClick}>
            <CalendarPlus size={17} aria-hidden />Zum Kalender
          </a>
        )}
        {aktionen.extra}
      </div>
    </div>
  </header>
);

// ── Strecke ──────────────────────────────────────────────────────────────────────────────────

export interface EventKennzahl { icon: React.ReactNode; label: string; wert: string }

/**
 * Strecke wie bei komoot: Reiter je Strecke oben, darunter die Kennzahlen der gewählten, dann
 * Karte und Höhenprofil. Ohne Streckenkarte steht dort das Foto oder der Startort. Die Reiter
 * schalten immer etwas um (mindestens die Kennzahlen), deshalb sind sie hier immer Knöpfe,
 * anders als auf dem Handy, wo Kennzahlen je Strecke in den Kacheln selbst stehen.
 */
export const EventStreckenBlock: React.FC<{
  id?: string;
  strecken: EventKopfStrecke[];
  gewaehlt: number;
  onWahl: (i: number) => void;
  kennzahlen: EventKennzahl[];
  bild: EventKopfBild;
  abgesagt?: boolean;
  /** „In Komoot öffnen“ für die gewählte Strecke. */
  routeLink?: { label: string; href: string } | null;
}> = ({ id, strecken, gewaehlt, onWahl, kennzahlen, bild, abgesagt, routeLink }) => {
  if (!bild && kennzahlen.length === 0 && strecken.length < 2) return null;
  return (
    <section className="edd-strecke" id={id}>
      <h2 className="edu-titel">{strecken.length > 1 ? 'Strecken' : 'Strecke'}</h2>
      {strecken.length > 1 && (
        <div className="edd-reiter" role="group" aria-label="Strecke wählen">
          {strecken.map((st, i) => (
            <button key={st.key} type="button" className={`edk-strecke${i === gewaehlt ? ' edk-strecke--an' : ''}`}
              aria-pressed={i === gewaehlt} onClick={() => onWahl(i)}>
              <span className="edk-strecke-gross">{st.gross}<small>{st.einheit}</small></span>
            </button>
          ))}
        </div>
      )}
      {/* Die Kennzahlen stehen über der Karte auf dem Seitengrund, nicht als eigene Leiste in
          ihr: mit nur einer Angabe war das ein weißer Streifen mit einer Zahl darin. */}
      {(kennzahlen.length > 0 || routeLink) && (
        <div className="edd-kennzahlen">
          {kennzahlen.map(k => (
            <div key={k.label} className="edd-kennzahl">
              <span className="edd-kennzahl-label">{k.icon}{k.label}</span>
              <strong>{k.wert}</strong>
            </div>
          ))}
          {routeLink && (
            <a className="edd-route-link" href={routeLink.href} target="_blank" rel="noopener noreferrer">
              {routeLink.label}<ExternalLink size={14} aria-hidden />
            </a>
          )}
        </div>
      )}
      {bild && (
      <div className="edd-karte">
        <EventKopfBildFlaeche bild={bild} abgesagt={abgesagt} gross />
      </div>
      )}
    </section>
  );
};

// ── Anmeldekarte ─────────────────────────────────────────────────────────────────────────────

/**
 * Rechte Spalte, läuft beim Scrollen mit (Airbnb, GetYourGuide, Eventbrite): Termin, Start, Ort,
 * dann der eine Hauptknopf. Ohne Anmeldelink führt er zum Veranstalter; bei geschlossener
 * Anmeldung ist er leise (`leise`), weil ein Sog auf ein geschlossenes Formular ein falsches
 * Versprechen wäre.
 */
export const EventAnmeldeKarte: React.FC<{
  termin: string;
  /** „bis Sonntag, 27. September 2026“ bei mehrtägigen Events. */
  terminZusatz?: string;
  start?: string;
  ort?: { zeile1: string; zeile2?: string; mapsUrl?: string };
  teilnehmer?: string;
  /** Anmeldestand als Chip. */
  anmeldung?: React.ReactNode;
  preisstufen?: React.ReactNode;
  primary?: EventDetailPrimaryAction;
  /** Statt eines Links ein eigener Hauptknopf („Fürs nächste Jahr benachrichtigen“). */
  primaryNode?: React.ReactNode;
  leise?: boolean;
  /** Glocke „Benachrichtigen, wenn die Anmeldung öffnet“. */
  glocke?: React.ReactNode;
  /** Kleine Karte vom Startort oben in der Karte. */
  karte?: { lat: number; lng: number } | null;
}> = ({ termin, terminZusatz, start, ort, teilnehmer, anmeldung, preisstufen, primary, primaryNode, leise, glocke, karte }) => (
  <div className="edd-anmeldung">
    {karte && <div className="edd-anmeldung-karte"><StartortKarte lat={karte.lat} lng={karte.lng} /></div>}
    <ul className="edd-fakten">
      <li>
        <CalendarDays size={18} aria-hidden />
        <span><strong>{termin}</strong>{terminZusatz && <span>{terminZusatz}</span>}</span>
      </li>
      {start && <li><Clock3 size={18} aria-hidden /><span><strong>Start {start}</strong></span></li>}
      {ort && (
        <li>
          <MapPin size={18} aria-hidden />
          <span><strong>{ort.zeile1}</strong>{ort.zeile2 && <span>{ort.zeile2}</span>}</span>
          {ort.mapsUrl && <a className="edd-fakt-link" href={ort.mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={`Route zu ${ort.zeile1}`}>Route<ExternalLink size={13} aria-hidden /></a>}
        </li>
      )}
      {teilnehmer && <li><Users size={18} aria-hidden /><span><strong>{teilnehmer} Teilnehmer</strong><span>bei der letzten Auflage</span></span></li>}
    </ul>
    {(anmeldung || preisstufen) && (
      <div className="edd-anmeldung-stand">
        {anmeldung}
        {preisstufen}
      </div>
    )}
    {(primaryNode || primary || glocke) && (
      <div className="edd-anmeldung-knoepfe">
        {primaryNode ?? (primary && <PrimaryKnopf primary={primary} leise={leise} />)}
        {glocke}
      </div>
    )}
  </div>
);

const PrimaryKnopf: React.FC<{ primary: EventDetailPrimaryAction; leise?: boolean; klein?: boolean }> = ({ primary, leise, klein }) => {
  const cls = `edd-hauptknopf${leise ? ' edd-hauptknopf--leise' : ''}${klein ? ' edd-hauptknopf--klein' : ''}`;
  return primary.internal
    ? <Link className={cls} to={primary.href} onClick={primary.onClick}>{primary.label}</Link>
    : <a className={cls} href={primary.href} target="_blank" rel="noopener noreferrer" onClick={primary.onClick}>{primary.label}<ExternalLink size={16} aria-hidden /></a>;
};

/** Anmeldestand als Chip; steht im Kopf auf dem Handy und in der Anmeldekarte am Rechner. */
export const AnmeldeStandChip: React.FC<{ stand: RegistrationBadge }> = ({ stand }) => (
  // Absage/ausgebucht bleiben in der lauten Versalien-Form — sie sind eine Warnung.
  // Der Anmeldestand ist eine Auskunft und steht deshalb in Satzschreibung mit Punkt.
  stand.tone === 'negative' || stand.tone === 'warning' ? (
    <span title={stand.long} style={{
      display: 'inline-flex', alignItems: 'center', fontFamily: "'Space Mono',monospace",
      fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', padding: '6px 12px',
      borderRadius: 999, color: '#fff', background: '#C0392B',
    }}>
      {stand.short}
    </span>
  ) : (
    <span title={stand.long} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 14,
      padding: '7px 14px', borderRadius: 999,
      ...(stand.tone === 'positive'
        ? { color: '#1B7A4B', background: 'rgba(31,138,91,0.12)' }
        : { color: '#6A6A6A', background: '#F1EEEC' }),
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor' }} />
      {stand.label}
    </span>
  )
);

/** Veranstalter rechts unter Unterkunft und Wetter: dieselben Zeilen wie auf dem Handy, als Karte. */
export const EventVeranstalterKarte: React.FC<{ veranstalter: UntenVeranstalter; id?: string }> = ({ veranstalter, id }) => (
  <div className="edd-seite-karte edd-veranstalter" id={id}>
    <span className="edd-seite-kicker">Veranstalter</span>
    <VeranstalterZeilen veranstalter={veranstalter} />
  </div>
);

// ── Sprungleiste ─────────────────────────────────────────────────────────────────────────────

/**
 * Erscheint unter dem Seitenkopf, sobald der Event-Kopf aus dem Bild ist (Tripadvisor, Zillow):
 * Titel, Termin und Sprünge zu den Abschnitten. Der Hauptknopf kommt erst dazu, wenn auch die
 * Anmeldekarte aus dem Bild ist (`mitKnopf`); solange sie zu sehen ist, wäre er ein zweiter Weg
 * zum selben Ziel.
 */
export const EventSprungLeiste: React.FC<{
  sichtbar: boolean;
  titel: string;
  termin: string;
  abschnitte: { id: string; label: string }[];
  primary?: EventDetailPrimaryAction;
  leise?: boolean;
  /** Hauptknopf zeigen: die Anmeldekarte ist aus dem Bild. */
  mitKnopf?: boolean;
  /** Im Design-Lab: im Fluss statt am Fenster festgemacht. */
  fest?: boolean;
}> = ({ sichtbar, titel, termin, abschnitte, primary, leise, mitKnopf, fest = true }) => {
  // Unter dem Seitenkopf, solange er zu sehen ist; ist er weggescrollt, ganz oben.
  const [oben, setOben] = useState(0);
  useEffect(() => {
    if (!fest || !sichtbar) return;
    const kopf = document.querySelector('.public-header');
    const messen = () => setOben(kopf ? Math.max(0, kopf.getBoundingClientRect().bottom) : 0);
    messen();
    // Am Dokument mit capture: auf dieser Seite scrollt body, nicht das Fenster (utils/seitenScroll).
    return beiSeitenScroll(messen);
  }, [fest, sichtbar]);
  return (
    <div className={`edd-leiste${sichtbar ? ' edd-leiste--sichtbar' : ''}${fest ? '' : ' edd-leiste--im-fluss'}`}
      style={fest ? { top: oben } : undefined} aria-hidden={!sichtbar}>
      <div className="edd-leiste-innen">
        <div className="edd-leiste-titel">
          <strong>{titel}</strong>
          <span>{termin}</span>
        </div>
        <nav className="edd-leiste-nav" aria-label="Abschnitte">
          {abschnitte.map(a => (
            <a key={a.id} href={`#${a.id}`} tabIndex={sichtbar ? undefined : -1} onClick={e => {
              const ziel = document.getElementById(a.id);
              if (!ziel) return;
              e.preventDefault();
              ziel.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}>{a.label}</a>
          ))}
        </nav>
        {primary && mitKnopf && <span className="edd-leiste-knopf"><PrimaryKnopf primary={primary} leise={leise} klein /></span>}
      </div>
    </div>
  );
};

/** Ob ein Element oben aus dem Fenster gescrollt ist (für die Sprungleiste). */
export function useOberhalbAusSicht(el: HTMLElement | null): boolean {
  const [aus, setAus] = useState(false);
  useEffect(() => {
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setAus(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, [el]);
  return aus;
}

// ── Wetter ───────────────────────────────────────────────────────────────────────────────────

const WETTER_ICONS: Record<string, LucideIcon> = {
  'clear-day': Sun, 'clear-night': Moon, 'partly-cloudy-day': CloudSun, 'partly-cloudy-night': CloudMoon,
  cloudy: Cloud, fog: CloudFog, wind: Wind, rain: CloudRain, sleet: CloudSnow, snow: CloudSnow,
  hail: CloudHail, thunderstorm: CloudLightning,
};

/**
 * Wetter am Eventtag (DWD über Bright Sky), mit Symbolen statt Emojis. Steht rechts unter der
 * Anmeldekarte, wie die Unterkunft: beides gehört zur Planung der Anreise, nicht zum Lesen.
 */
export const EventWetter: React.FC<{ wetter: WeatherForecast }> = ({ wetter }) => {
  const Icon = WETTER_ICONS[wetter.icon] ?? Cloud;
  return (
    <div className="edd-wetter">
      <span className="edd-seite-kicker">Wetter am Eventtag</span>
      <span className="edd-wetter-icon"><Icon size={30} aria-hidden /></span>
      <div className="edd-wetter-haupt">
        <strong>{Math.round(wetter.tempMax)}°<small> / {Math.round(wetter.tempMin)}°</small></strong>
        <span>{weatherLabel(wetter.condition)}</span>
      </div>
      <div className="edd-wetter-neben">
        <span><Droplets size={15} aria-hidden />{wetter.precipitation} mm</span>
        <span><Wind size={15} aria-hidden />{wetter.windMax} km/h</span>
      </div>
      <span className="edd-wetter-quelle">Quelle: {wetter.source}</span>
    </div>
  );
};
